<template>
	<div tabindex="0" class="hide-outline summary-main">
		<p v-if="round.done && !saving" class="title">
			{{ $t('summary.result_title') }} - {{ round.name }}
		</p>
		<main class="column">
			<div class="charts-container">
				<div class="chart">
					<p class="title" style="text-align: left">{{ $t('summary.chart_flaws') }}</p>
					<PieChart
						:width="150"
						:height="250"
						:responsive="false"
						:chartData="flawData"
						:labels="flawLabels"
						:colors="chartColors"
					/>
				</div>
				<div class="chart">
					<p class="title" style="text-align: left">{{ $t('summary.chart_items') }}</p>
					<PieChart
						:width="150"
						:height="250"
						:responsive="false"
						:chartData="itemData"
						:labels="itemLabels"
						:colors="chartColors"
					/>
				</div>
				<div class="chart">
					<p class="title" style="text-align: left">{{ $t('summary.chart_questions') }}</p>
					<PieChart
						:width="150"
						:height="250"
						:responsive="false"
						:chartData="questionData"
						:labels="questionlabels"
						:colors="chartColors"
					/>
				</div>
				<div class="info">
					<p class="title">{{ $t('summary.summary_title') }}</p>
					<div class="summary">
						<p>{{ totProtections.length }} {{ $t('summary.total_items') }}</p>
						<p>
							{{ checkedItems.length }} {{ $t(checkedItems.length === 1 ? 'summary.item_checked_single' : 'summary.item_checked_plural') }}
						</p>
						<p>{{ questions.length }} {{ $t('summary.total_questions') }}</p>
						<p>
							{{ questionData[0] + questionData[1] }}
							{{ $t((questionData[0] + questionData[1]) === 1 ? 'summary.question_answered_single' : 'summary.question_answered_plural') }}
						</p>
						<p>
							{{ flawData[1] }}
							{{ $t(flawData[1] === 1 ? 'summary.flaw_found_single' : 'summary.flaw_found_plural') }}
						</p>

						<p></p>
					</div>
					<div class="summary-foot">
						<div v-if="!round.done || saving">
							<p>
								{{ totProtections.length - checkedItems.length }}
								{{
									$t(
									totProtections.length - checkedItems.length === 1
										? 'summary.item_unchecked_single'
										: 'summary.item_unchecked_plural'
									)
								}}
							</p>
							<div v-if="$isAllowedTo('round_mark_remaining_items_as_ok')">
								<button class="button is-fullwidth" @click="allOk">
									<span class="icon"><i class="fas fa-tasks"/></span>
									<span>{{ $t('summary.mark_remaining_ok') }}</span>
								</button>
							</div>
							<div class="buttons has-addons">
								<button
									class="button is-success is-half"
									@click="finish"
									:disabled="disableDone"
									:class="saving ? 'is-loading' : ''"
									:title="!isOnline ? $t('summary.finish_requires_online_tooltip') : null"
								>
									<span
										class="icon is-white"
									><i
										class="fas fa-clipboard-check"
									/></span>
									<span>{{ $t('summary.finish_round') }}</span>
								</button>
								<button
									class="button is-half"
									@click="pause"
									:class="saving ? 'is-loading' : ''"
								>
									<span class="icon"><i class="fas fa-pause"/></span>
									<span>{{ $t('summary.pause_round') }}</span>
								</button>
							</div>
						
						</div>
						<div class="done-summary-foot" v-if="round.done && !saving">
							<p>{{ $t('summary.completed') }}: {{ moment(round.done).format("YYYY-MM-DD") }}</p>
							<p>{{ $t('summary.responsible') }}: {{ round.roleName }}</p>
							<p>{{ $t('summary.user') }}: {{ round.userName }}</p>
							<div class="buttons has-addons" v-if="!printMode">
								<button class="button is-info" @click="print">
									<span class="icon"><i class="fas fa-print"/></span>
									<span>{{ $t('summary.print') }}</span>
								</button>
								<button class="button is-dark" @click="back">
									<span class="icon"><i class="fas fa-backward"/></span>
									<span>{{ $t('summary.back') }}</span>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
			<ItemFlawSummary :infos="infos" :round="round" />
			<QuestionSummaryList :checklists="checklists" />
		</main>
	</div>
</template>

<script>
import PieChart from '@/components/PieChart';
import ItemFlawSummary from './ItemFlawSummary.vue';
import QuestionSummaryList from './QuestionSummaryList';
import { mapGetters } from 'vuex';

export default {
	components: {
		PieChart,
		ItemFlawSummary,
		QuestionSummaryList
	},
	props: {
		infos: Array,
		checklists: Array,
		success: Boolean,
		saving: Boolean,
		isOnline: Boolean
	},
	computed: {
		...mapGetters('appStore', ['isMobile', 'printMode']),
		...mapGetters('roundStore', ['round']),
		...mapGetters('roundHistoryInfoStore', ['roundHistoryInfos']),
		failed() {
			return this.success === false || (this.success === null && !this.saving)
				? true
				: false;
		},
		finishDisabled() {
			return this.saving || !this.isOnline || this.disableDone;
		},
		totProtections() {
			const infos = this.infos || [];
			return infos
				.flatMap(info => info.roundHistoryItems || [])
				.filter(item =>
				item &&
				item.id &&
				item.positionNumber != null &&
				item.inventoryType
				);
		},
		flawData() {
			let data = [0, 0, 0];

			data[0] = this.questionData[0] + this.itemData[0];
			data[1] = this.questionData[1] + this.itemData[1];

			return data;
		},
		flawLabels() {
			return [this.$t('summary.label_no_action'), this.$t('summary.label_flaws')];
		},
		itemData() {
			let data = [0, 0, 0];

			(this.roundHistoryInfos || []).forEach(info => {
				(info.roundHistoryItems || [])
				.filter(item =>
					item &&
					item.id &&
					item.positionNumber != null &&
					item.inventoryType
				)
				.forEach(item => {
					if (item.controlled || item.continueWithoutAdding) {
						if (this.itemHasReportableFlaw(item)) data[1]++;
						else data[0]++;
					} else {
						data[2]++;
					}
				});
			});

			return data;
		},
		itemLabels() {
			return [
				this.$t('summary.label_ok'),
				this.$t('summary.label_flaws'),
				this.$t('summary.label_unchecked')
			];
		},
		questionData() {
			let data = [0, 0, 0];
			this.questions.forEach(question => {
				if (question.answer === question.positive || question.answer === '') {
					data[0]++;
				} else if (question.answer === question.negative) {
					data[1]++;
				} else {
					data[2]++;
				}
			});

			return data;
		},
		checkedItems() {
			let list = [];
			this.totProtections.forEach(item => {
				if (item.controlled || item.continueWithoutAdding) {
					list.push(item);
				}
			});

			return list;
		},
		questions() {
			let list = [];
			this.checklists.forEach(checklist => {
				checklist.roundHistoryQuestions.forEach(question => {
					list.push(question);
				});
			});

			return list;
		},
		questionlabels() {
			return [
				this.$t('summary.label_positive'),
				this.$t('summary.label_negative'),
				this.$t('summary.label_unanswered')
			];
		},
		chartColors() {
			return ['#7bca78', '#da6060', '#d3d3d3'];
		},
		disableDone() {
			const items = this.totProtections.length - this.checkedItems.length;
			const questions = this.questionData[0] + this.questionData[1] - this.questions.length;

			return items !== 0 || questions !== 0;
		}
	},
	methods: {
		itemHasReportableFlaw(item) {
			const flaws = item?.flaws || [];
			if (!flaws.length) return false;

			if (item?.continueWithoutAdding) {
				return flaws.some(flaw => !flaw?.done);
			}

			return flaws.some(flaw => {
				const created = flaw?.created;
				if (!created || created <= this.round.created) return false;
				if (this.round.done && created >= this.round.done) return false;
				return true;
			});
		},
		allOk() {
			this.$emit('allOk');
		},
		pause() {
			this.$emit('pauseRound');
		},
		finish() {
			this.$emit('finishRound');
		},
		back() {
			this.$emit('back');
		},
		print() {
			this.$emit('print');
		}
	}
};
</script>

<style lang="scss" scoped>
.helper {
  font-size: 14px;
  font-weight: bold;

  &.success {
    color: green;
  }

  &.failed {
    color: red;
  }
}

.charts-container {
  display: flex;
  flex-wrap: wrap;
  max-width: 100%;

  .info {
    flex: 1;
    max-width: 350px;

    p.title {
      text-align: left;
      margin-bottom: 0;
      font-size: 1.5rem;
      font-weight: 400;
    }

    .summary {
      margin-top: 4px;
      min-width: 180px;
    }
  }

  .chart {
    flex: 1;
    width: 25%;
    min-width: 210px;
    max-width: 210px;

    p.title {
      text-align: left;
      margin-bottom: 0;
      font-size: 1.5rem;
      font-weight: 400;
    }

    .chart-size {
      position: relative;
      width: 100%;
    }
  }
}

.summary-foot {
  margin-top: 1.5rem;
}

.done-summary-foot {
  .button {
    margin-top: 2rem;
  }
}

.buttons {
  margin-top: 1.5rem;

  .button.is-half {
    width: 50%;

    .icon.is-white i {
      color: white;
    }
  }
}

@media print {
  .summary-main {
    position: absolute;
    margin-top: -0.75rem;
  }
}
</style>

<style lang="scss">
.chart-container {
  width: 200px;
}
</style>
